import { useActiveAddress, useConnection } from "@arweave-wallet-kit/react"
import styled from "@emotion/styled"
import CloseIcon from "@mui/icons-material/Close"
import ShareIcon from "@mui/icons-material/Share"
import {
  Box,
  Dialog,
  Divider,
  Skeleton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { Paper } from "@mui/material"
import { createDataItemSigner, message, result } from "@permaweb/aoconnect"
import { useQuery, useMutation } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import Web3 from "web3"

// Existing components
import { OrderStatus } from "../ao/pool"
import { RichTokenInfo } from "../ao/router"
import Alert from "../components/Alert"
import { BeeLetter } from "../components/BeeLetter"
import Button from "../components/Button"
import { IconButton } from "../components/IconButton"
import { SuccessIcon } from "../components/SuccessIcon"
import { TitleBar } from "../components/TitleBar"
import TokenInputEVM from "../components/TokenInputEVM"
import TokenLogo from "../components/TokenLogo"
import TokenPill from "../components/TokenPill"

// New components
import {
  AOWalletConnect,
  DelegationCapacity,
  DelegationSlider,
  BOTGStatsPanel,
} from "../components/mint"

// Contexts & Hooks
import { useMintFlow, MintFlowProvider } from "../contexts/mint-flow"
import { DaiBridge_ABI, Erc20_ABI, ETH_CONTRACTS, StEthBridge_ABI } from "../helpers/config"
import { arweaveToEVMBytes } from "../helpers/utils"
import { useDelegationCapacity } from "../hooks/use-delegation-capacity"
import { useTokenInfo } from "../hooks/use-token-info"
import { useEthereumProvider } from "../providers/EthereumProvider"

// Utils & Config
import { DAI, stETH, DELEGATE_PROCESS, BOTEGA_PROJECT_ADDRESS } from "../settings"
import { NumberFont, TitleFont } from "../theme"
import {
  formatTicker,
  formatNumberAuto,
  parseNumberAsBigInt,
  formatAddress,
  parseBigIntAsNumber,
} from "../utils/format"

// Types

type MintOrderType = "deposit" | "withdraw"

// Wrap the actual component with the context provider
export default function MintPageWrapper() {
  return (
    <MintFlowProvider>
      <MintPage />
    </MintFlowProvider>
  )
}

function MintPage() {
  // Get context values
  const {
    // Unused but keep for future use
    // currentStep,
    // setCurrentStep,
    delegationPercentage,
    setDelegationPercentage,
    delegationUnits,
    setDelegationUnits,
    setAOWalletConnected,
    setEthWalletConnected,
    delegationComplete,
    setDelegationComplete,
    depositComplete,
    setDepositComplete,
  } = useMintFlow()

  useEffect(() => {
    document.title = "Deposit Assets | Delegate AO Capacity · Botega"
  }, [])

  // inputs
  const [fromValue, setFromValue] = useState<string>("")

  // token selections
  const [searchParams, setSearchParams] = useSearchParams()
  const fromTokenId = searchParams.get("token") || "0x6b175474e89094c44da98b954eedeac495271d0f"
  const orderType = (searchParams.get("type") || "deposit") as MintOrderType

  // token infos
  const [fromToken] = useTokenInfo(fromTokenId)

  // all tokens
  const tokens: RichTokenInfo[] = useMemo(
    () => [
      {
        ...DAI,
        approved: true,
      },
      {
        ...stETH,
        approved: true,
      },
    ],
    [],
  )

  // wallet connections
  const { connect } = useConnection()
  const activeAoAddress = useActiveAddress()
  const ethProvider = useEthereumProvider()
  const activeAddr = ethProvider.walletAddress

  // Update wallet connection states
  useEffect(() => {
    setAOWalletConnected(!!activeAoAddress)
    setEthWalletConnected(!!activeAddr)
  }, [activeAoAddress, activeAddr, setAOWalletConnected, setEthWalletConnected])

  // normalized input value
  const fromValueNormalized = useMemo<bigint>(() => {
    if (!fromToken) return 0n
    try {
      return parseNumberAsBigInt(fromValue, fromToken.denomination)
    } catch {
      return 0n
    }
  }, [fromValue, fromToken])

  // Get delegation capacity data
  const {
    data: delegationCapacity,
    isLoading: isLoadingDelegation,
    refetch: refetchDelegationCapacity,
  } = useDelegationCapacity(activeAoAddress)

  // Determine the existing Botega delegation factor
  const existingBotegaFactor = useMemo(() => {
    if (!delegationCapacity?.delegationPrefs) return 0

    // Find the delegation to the Botega project address
    const botegaDelegation = delegationCapacity.delegationPrefs.find(
      (pref: { walletTo: string }) => pref.walletTo === BOTEGA_PROJECT_ADDRESS,
    )

    return botegaDelegation ? botegaDelegation.factor : 0
  }, [delegationCapacity?.delegationPrefs])

  // Track current view (delegation or deposit)
  const [currentView, setCurrentView] = useState<"delegation" | "deposit">("delegation")

  // Update view based on delegation capacity
  useEffect(() => {
    if (delegationCapacity && !isLoadingDelegation) {
      if (existingBotegaFactor < 10000 && !delegationComplete) {
        setCurrentView("delegation")
      } else {
        setCurrentView("deposit")
      }
    }
  }, [delegationCapacity, isLoadingDelegation, delegationComplete, existingBotegaFactor])

  // Set delegation units when capacity data updates
  useEffect(() => {
    if (delegationCapacity) {
      const units = Math.floor((delegationPercentage / 100) * delegationCapacity.available)
      setDelegationUnits(units)
    }
  }, [delegationCapacity, delegationPercentage, setDelegationUnits])

  // balances
  const { data: fromBalance, refetch: refetchFromBalance } = useQuery({
    queryKey: [`balance_${fromTokenId}`, activeAddr],
    enabled: !!activeAddr && !!fromTokenId,
    queryFn: async () => {
      if (!activeAddr) throw new Error("No active address")
      if (!fromTokenId) throw new Error("No from token id")
      if (!ethProvider.web3Provider) throw new Error("Provider missing")

      try {
        const web3 = new Web3(ethProvider.web3Provider)
        const tokenContract = new web3.eth.Contract(Erc20_ABI, fromTokenId)
        const bal = await tokenContract.methods.balanceOf(activeAddr).call()
        return BigInt(bal as any)
      } catch (e) {
        console.error(e)
        return 0n
      }
    },
  })

  async function getEthDeposits() {
    if (!activeAddr) throw new Error("No active address")
    if (!ethProvider.web3Provider) throw new Error("Provider missing")

    try {
      const web3 = new Web3(ethProvider.web3Provider)
      const bridgeAddress = ETH_CONTRACTS.stEthBridge
      const bridgeABI = StEthBridge_ABI
      const bridgeContract = new web3.eth.Contract(bridgeABI, bridgeAddress)
      const usersData = (await bridgeContract.methods.usersData(activeAddr, 0).call()) as any

      return BigInt(usersData.deposited)
    } catch (e) {
      console.error(e)
      return 0n
    }
  }

  async function getDaiDeposits() {
    if (!activeAddr) throw new Error("No active address")
    if (!ethProvider.web3Provider) throw new Error("Provider missing")

    try {
      const web3 = new Web3(ethProvider.web3Provider)
      const bridgeAddress = ETH_CONTRACTS.daiBridge
      const bridgeABI = DaiBridge_ABI
      const bridgeContract = new web3.eth.Contract(bridgeABI, bridgeAddress)
      const usersData = (await bridgeContract.methods.usersData(activeAddr, 0).call()) as any

      return BigInt(usersData.deposited)
    } catch (e) {
      console.error(e)
      return 0n
    }
  }

  const { data: depositedBalance, refetch: refetchDepositedBalance } = useQuery({
    queryKey: [`deposited_balance_${fromTokenId}`, activeAddr],
    enabled: !!activeAddr && !!fromTokenId,
    queryFn: async () => {
      if (!activeAddr) throw new Error("No active address")
      if (!fromTokenId) throw new Error("No from token id")
      if (!ethProvider.web3Provider) throw new Error("Provider missing")

      try {
        const ethDeposits = await getEthDeposits()
        const daiDeposits = await getDaiDeposits()

        return {
          dai: daiDeposits,
          stEth: ethDeposits,
        }
      } catch (e) {
        console.error(e)
        return {
          dai: 0n,
          stEth: 0n,
        }
      }
    },
  })

  // button validation
  const validationError = useMemo(() => {
    if (!fromToken) {
      return "Select a token"
    }
    if (!fromValueNormalized) {
      return "Enter an amount"
    }
    if (!activeAoAddress) {
      return "Connect AO wallet first"
    }
    // Only require delegation completion if the user has delegation capacity available
    // and we're viewing the delegation screen (not the deposit screen)
    if (
      !delegationComplete &&
      orderType === "deposit" &&
      delegationCapacity &&
      delegationCapacity.available > 0 &&
      currentView === "delegation"
    ) {
      return "Set delegation first"
    }
    if (
      typeof fromBalance === "bigint" &&
      fromValueNormalized > fromBalance &&
      orderType === "deposit"
    ) {
      return "Insufficient funds"
    }
    if (
      typeof depositedBalance === "bigint" &&
      fromValueNormalized > depositedBalance &&
      orderType === "withdraw"
    ) {
      return "Insufficient deposited funds"
    }

    return ""
  }, [
    fromToken,
    fromValueNormalized,
    fromBalance,
    depositedBalance,
    activeAoAddress,
    delegationComplete,
    orderType,
    delegationCapacity,
    currentView,
  ])

  // confirm popover
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  async function showPopover() {
    if (validationError) return

    // Check if allowance is needed
    if (activeAddr && ethProvider.web3Provider && fromValueNormalized > 0n && fromToken) {
      try {
        const web3 = new Web3(ethProvider.web3Provider)
        const isDAI = fromTokenId === DAI.id
        const bridgeAddress = isDAI ? ETH_CONTRACTS.daiBridge : ETH_CONTRACTS.stEthBridge
        const tokenAddress = isDAI ? ETH_CONTRACTS.dai : ETH_CONTRACTS.stEth

        const tokenContract = new web3.eth.Contract(Erc20_ABI, tokenAddress)
        const allowance = await tokenContract.methods.allowance(activeAddr, bridgeAddress).call()

        setNeedsAllowance(BigInt(allowance as any) < fromValueNormalized)
      } catch (error) {
        console.error("Error checking allowance:", error)
        setNeedsAllowance(true)
      }
    }

    setConfirmDialogOpen(true)
  }

  // Track loading state during view transition
  const [isViewTransitioning, setIsViewTransitioning] = useState<boolean>(false)

  function closePopover() {
    if (orderStatus === "preparing" || orderStatus === "pending") return

    if (orderStatus === "success" || orderStatus === "error") {
      // After successful deposit, check if delegation is now available
      if (
        orderStatus === "success" &&
        orderType === "deposit" &&
        delegationCapacity &&
        delegationCapacity.available > 0 &&
        !delegationComplete
      ) {
        // Set transition loading state
        setIsViewTransitioning(true)

        // Close dialog first
        setOrderStatus(undefined)
        setConfirmDialogOpen(false)
        resetForm()

        // Add a small delay for better UX during transition
        setTimeout(() => {
          // Switch view to delegation after deposit if capacity is available
          setCurrentView("delegation")
          setIsViewTransitioning(false)
        }, 500)

        return
      }

      resetForm()
    }

    resetState()
    setConfirmDialogOpen(false)
  }

  // Update delegation settings
  const handleDelegationChange = (percentage: number, units: number) => {
    setDelegationPercentage(percentage)
    setDelegationUnits(units)
  }

  // Use useMutation for delegation process
  const delegationMutation = useMutation({
    mutationFn: async (customDelegationPercentage: number | undefined): Promise<boolean> => {
      if (!activeAoAddress) return false

      const delPercentage = customDelegationPercentage || delegationPercentage

      // Check if we need to clear other delegations
      const totalRequested = Math.floor(
        (delPercentage / 100) * (delegationCapacity?.totalCapacity || 10000),
      )
      const existingBotegaUnits = Math.floor(
        (existingBotegaFactor / 10000) * (delegationCapacity?.totalCapacity || 10000),
      )
      const newDelegationUnits = totalRequested - existingBotegaUnits
      const exceedsAvailableCapacity = newDelegationUnits > (delegationCapacity?.available || 0)

      // If the selected delegation exceeds available capacity,
      // we need to reduce other delegations proportionally
      if (exceedsAvailableCapacity && delegationCapacity?.delegationPrefs) {
        // Get all other (non-Botega) delegations
        const otherDelegations = delegationCapacity.delegationPrefs.filter(
          (pref: { walletTo: string }) => pref.walletTo !== BOTEGA_PROJECT_ADDRESS,
        )

        if (otherDelegations.length > 0) {
          // Calculate how much we need to take from other delegations
          const totalOtherFactors = otherDelegations.reduce(
            (sum: number, pref: { factor: number }) => sum + pref.factor,
            0,
          )

          // If there are other delegations to adjust
          if (totalOtherFactors > 0) {
            const excessUnits = newDelegationUnits - (delegationCapacity?.available || 0)
            const reductionRatio =
              excessUnits / (delegationCapacity.delegated - existingBotegaFactor)

            console.log("totalOtherFactors", totalOtherFactors)
            console.log("excessUnits", excessUnits)
            console.log("reductionRatio", reductionRatio)

            // We need to ensure the total reduction exactly equals the excess units
            // First calculate the proportional reductions
            let totalReduction = 0
            const delegationsWithReductions = otherDelegations.map((pref) => {
              // Calculate the reduction for this delegation
              let reduction = Math.floor(pref.factor * reductionRatio)

              // Ensure at least 1 unit reduction for very small values if needed
              if (excessUnits > 0 && reduction === 0) {
                reduction = 1
              }

              // If the new factor would be less than 500 (5%), set it to 0
              const newFactor =
                pref.factor - reduction < 500 ? 0 : Math.max(0, pref.factor - reduction)
              totalReduction += pref.factor - newFactor

              return {
                ...pref,
                reduction,
                newFactor,
              }
            })

            // If we still need more reduction (due to rounding), take from the largest delegations
            const remainingExcess = excessUnits - totalReduction
            if (remainingExcess > 0) {
              // Sort by factor size descending to reduce from the largest delegations first
              delegationsWithReductions.sort((a, b) => b.newFactor - a.newFactor)

              let remainingToReduce = remainingExcess
              for (let i = 0; i < delegationsWithReductions.length && remainingToReduce > 0; i++) {
                const delegation = delegationsWithReductions[i]
                // Only reduce if we can (keeping at least 500 units or setting to 0)
                if (delegation.newFactor > 500) {
                  const additionalReduction = Math.min(
                    remainingToReduce,
                    delegation.newFactor - 500,
                  )
                  delegation.newFactor -= additionalReduction
                  delegation.reduction += additionalReduction
                  remainingToReduce -= additionalReduction
                } else if (delegation.newFactor > 0) {
                  // If we can't keep 500 units, set to 0
                  delegation.reduction += delegation.newFactor
                  delegation.newFactor = 0
                  remainingToReduce -= delegation.newFactor
                }
              }
            }

            // Now process the updated delegations
            const updatePromises = delegationsWithReductions.map(async (delegation) => {
              console.log("Reducing delegation for:", delegation.walletTo)
              console.log("Old factor:", delegation.factor)
              console.log("Reduction:", delegation.reduction)
              console.log("New factor:", delegation.newFactor)

              // Apply the new reduced factor
              const msgId = await message({
                process: DELEGATE_PROCESS,
                tags: [
                  {
                    name: "Action",
                    value: "Set-Delegation",
                  },
                ],
                data: JSON.stringify({
                  walletFrom: activeAoAddress,
                  walletTo: delegation.walletTo,
                  factor: delegation.newFactor,
                }),
                signer: createDataItemSigner(window.arweaveWallet),
              })

              const res = await result({
                message: msgId,
                process: DELEGATE_PROCESS,
              })

              return { msgId, res, walletTo: delegation.walletTo, newFactor: delegation.newFactor }
            })

            const updateResults = await Promise.all(updatePromises)
            console.log("Updated other delegations:", updateResults)
          }
        }
      }

      // Convert percentage to factor out of 10000
      const factor = Math.floor(delPercentage * 100)

      console.log(`Setting delegation to Botega with factor: ${factor} (${delPercentage}%)`)
      console.log(
        `This represents ${delegationUnits} units out of ${delegationCapacity?.totalCapacity || 10000}`,
      )

      // Set new delegation to Botega
      const msgId = await message({
        process: DELEGATE_PROCESS,
        tags: [
          {
            name: "Action",
            value: "Set-Delegation",
          },
        ],
        data: JSON.stringify({
          walletFrom: activeAoAddress,
          walletTo: BOTEGA_PROJECT_ADDRESS,
          factor: factor,
        }),
        signer: createDataItemSigner(window.arweaveWallet),
      })

      console.log("Delegation message ID:", msgId)

      const res = await result({
        message: msgId,
        process: DELEGATE_PROCESS,
      })

      console.log("Delegation result:", res)

      await refetchDepositedBalance()
      refetchFromBalance()
      await refetchDelegationCapacity()

      return true
    },
    onSuccess: () => {
      // Mark delegation as complete
      setDelegationComplete(true)

      // After successful delegation, switch to deposit view if there's no deposit yet
      if (!depositComplete) {
        // Set transition loading state
        setIsViewTransitioning(true)

        // Add a small delay for better UX during transition
        setTimeout(() => {
          setCurrentView("deposit")
          setIsViewTransitioning(false)
        }, 500)
      }
    },
    onError: (error) => {
      console.error("Error setting delegation:", error)
      alert("Failed to set delegation. Please try again.")
    },
  })

  // Handle delegation button click
  const handleCompleteDelegation = () => {
    delegationMutation.mutate(undefined)
  }

  // order state
  const [orderId, setOrderId] = useState<string>()
  const [orderStatus, setOrderStatus] = useState<OrderStatus>()
  const [orderError, setOrderError] = useState<string>()
  const [needsAllowance, setNeedsAllowance] = useState<boolean>(true)

  const changeViewToDeposit = () => {
    if (delegationMutation.isPending) return

    setIsViewTransitioning(true)
    setTimeout(() => {
      setCurrentView("deposit")
      setIsViewTransitioning(false)
    }, 500)
  }

  async function handleSubmit() {
    if (!!validationError || !ethProvider.web3Provider || !activeAddr) return

    console.log("Order type: ", orderType)

    try {
      // If no delegation capacity is available, set depositComplete to true after successful deposit
      const noDelegationCapacity = delegationCapacity && delegationCapacity.available === 0

      setOrderStatus("pendingWalletConfirmation")

      const web3 = new Web3(ethProvider.web3Provider)
      await ethProvider.ensureMainnet()

      const isDAI = fromTokenId === DAI.id
      const bridgeAddress = isDAI ? ETH_CONTRACTS.daiBridge : ETH_CONTRACTS.stEthBridge
      const bridgeABI = isDAI ? DaiBridge_ABI : StEthBridge_ABI
      const tokenAddress = isDAI ? ETH_CONTRACTS.dai : ETH_CONTRACTS.stEth

      const tokenContract = new web3.eth.Contract(Erc20_ABI, tokenAddress)
      const bridgeContract = new web3.eth.Contract(bridgeABI, bridgeAddress)

      if (orderType === "deposit") {
        const allowance = await tokenContract.methods.allowance(activeAddr, bridgeAddress).call()
        if (BigInt(allowance as any) < fromValueNormalized) {
          setOrderStatus("pendingWalletConfirmation")
          const approval = await tokenContract.methods
            .approve(bridgeAddress, fromValueNormalized)
            .send({ from: activeAddr })
          await checkTransactionReceipt(approval.transactionHash, ethProvider)
          setNeedsAllowance(false)
        }

        const stake = await bridgeContract.methods
          .stake(0, fromValueNormalized, arweaveToEVMBytes(activeAoAddress))
          .send({ from: activeAddr })
        console.log("Stake transaction:", stake)
        setOrderStatus("pending")
        await checkTransactionReceipt(stake.transactionHash, ethProvider)
        console.log("Stake transaction confirmed:", stake)

        // Mark deposit as complete
        setDepositComplete(true)

        // If no delegation capacity was available initially, refetch to check if capacity is now available
        if (noDelegationCapacity) {
          await refetchDepositedBalance()
          refetchFromBalance()
          await refetchDelegationCapacity()
        }

        setOrderId(stake.transactionHash)
        setOrderStatus("success")
      } else {
        const withdraw = await bridgeContract.methods
          .withdraw(0, fromValueNormalized, arweaveToEVMBytes(activeAoAddress))
          .send({
            from: activeAddr,
          })
        console.log("Withdraw transaction:", withdraw)
        setOrderStatus("pending")
        await checkTransactionReceipt(withdraw.transactionHash, ethProvider)
        console.log("Withdraw transaction confirmed:", withdraw)

        setOrderId(withdraw.transactionHash)
        setOrderStatus("success")
      }

      refetchFromBalance()
      refetchDepositedBalance()
    } catch (error) {
      if (!(error instanceof Error)) {
        setOrderError(String(error))
        setOrderStatus("error")
        return
      }

      if (error.message.includes("User denied transaction")) {
        setOrderStatus(undefined)
        return
      }

      setOrderStatus("error")
      setOrderError(error.message)
    }
  }

  // reset inputs
  function resetForm() {
    setFromValue("")
  }

  // reset order state
  function resetState() {
    setOrderStatus(undefined)
    setOrderError(undefined)
    setOrderId(undefined)
  }

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  return (
    <Page style={{ position: "relative" }}>
      <TitleBar
        variant="solid"
        title="Deposit Assets | Delegate AO Capacity | Earn $BOTG Tokens"
        sx={{ p: 1, border: 0 }}
      />
      {/* Main content */}
      <Stack
        component={Paper}
        elevation={0}
        sx={{ p: 2, pt: 1, mb: 2, width: "100%", borderTopRightRadius: 0, borderTopLeftRadius: 0 }}
      >
        {/* Step 1: Connect AO Wallet */}
        {!activeAoAddress ? (
          <AOWalletConnect activeAoAddress={activeAoAddress} onConnect={connect} />
        ) : (
          <>
            {orderType === "deposit" && (
              <Stack gap={1.5} width="100%">
                {/* Delegation Capacity Stats */}
                <DelegationCapacity
                  depositedBalance={depositedBalance || { dai: 0n, stEth: 0n }}
                  existingBotegaFactor={existingBotegaFactor}
                  isLoading={isLoadingDelegation}
                  delegationMutation={delegationMutation}
                  changeViewToDeposit={changeViewToDeposit}
                  ethAddress={activeAddr}
                  connectEthWallet={() => ethProvider.setWalletModalVisible(true)}
                />

                {isLoadingDelegation || isViewTransitioning ? (
                  /* Loading skeleton while delegation capacity data loads or view transitions */
                  <Stack gap={2} sx={{ width: "100%" }}>
                    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
                    <Skeleton
                      variant="rectangular"
                      height={48}
                      width="60%"
                      sx={{ borderRadius: 1 }}
                    />
                    <Skeleton variant="rectangular" height={55} sx={{ borderRadius: 1 }} />
                  </Stack>
                ) : currentView === "delegation" ? (
                  /* Show delegation form if capacity is available and delegation not complete */
                  <Stack gap={2} sx={{ width: "100%" }}>
                    <DelegationSlider
                      availableCapacity={delegationCapacity?.available || 0}
                      totalCapacity={delegationCapacity?.totalCapacity || 0}
                      defaultPercentage={delegationPercentage}
                      onChange={handleDelegationChange}
                      existingBotegaFactor={existingBotegaFactor}
                    />

                    {/* Wallet Info */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{
                        minHeight: 30,
                      }}
                      alignItems="center"
                    >
                      <Typography variant="body2" color="text.secondary">
                        Using Arweave Wallet
                      </Typography>
                      <Typography fontFamily={NumberFont} variant="body2">
                        {formatAddress(activeAoAddress, 8)}
                      </Typography>
                    </Stack>

                    {/* Set Delegation Button */}
                    <ContinueButton
                      onClick={handleCompleteDelegation}
                      className={delegationMutation.isPending ? "secondary" : "accent"}
                      disabled={delegationMutation.isPending}
                    >
                      {delegationMutation.isPending ? "Setting delegation..." : "Set Delegation"}
                    </ContinueButton>

                    {/* Skip link */}
                    <Typography
                      variant="body2"
                      color={delegationMutation.isPending ? "text.disabled" : "secondary"}
                      align="center"
                      sx={{
                        cursor: delegationMutation.isPending ? "default" : "pointer",
                        textDecoration: delegationMutation.isPending ? "none" : "underline",
                        opacity: delegationMutation.isPending ? 0.6 : 1,
                      }}
                      onClick={() => changeViewToDeposit()}
                    >
                      Skip for now and deposit more assets
                    </Typography>
                  </Stack>
                ) : (
                  /* Show deposit form if delegation is complete or no capacity available */
                  <Stack gap={1} sx={{ width: "100%" }}>
                    {/* Token Input */}
                    <Box
                      sx={{
                        background: "var(--mui-palette-background-default)",
                        borderRadius: "var(--mui-shape-borderRadius)",
                      }}
                    >
                      <TokenInputEVM
                        label="You Deposit"
                        tokenID={fromTokenId}
                        onTokenIdChange={(tokenId) => {
                          if (!tokenId) searchParams.delete("token")
                          else searchParams.set("token", tokenId)
                          setSearchParams(searchParams, { replace: true })
                        }}
                        tokenInfo={fromToken}
                        tokens={tokens}
                        autoFocus
                        onSubmit={showPopover}
                        value={fromValue}
                        onChange={(newValue) => {
                          setFromValue(newValue)
                        }}
                        balance={fromBalance}
                      />
                    </Box>

                    <Stack>
                      {/* Current Deposit Info */}
                      {activeAddr && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{
                            minHeight: 30,
                          }}
                          alignItems="center"
                        >
                          <Typography variant="body2" color="text.secondary">
                            Currently Deposited
                          </Typography>
                          {depositedBalance !== undefined && fromToken?.id ? (
                            <div>
                              {formatNumberAuto(
                                parseBigIntAsNumber(
                                  depositedBalance[fromToken?.id === DAI.id ? "dai" : "stEth"],
                                  fromToken.denomination || 18,
                                ),
                              )}{" "}
                              {fromToken.ticker}
                            </div>
                          ) : (
                            <Skeleton width={60} />
                          )}
                        </Stack>
                      )}

                      {/* Wallet Info */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{
                          minHeight: 30,
                        }}
                        alignItems="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Ethereum Wallet
                        </Typography>
                        <Typography fontFamily={NumberFont} variant="body2">
                          {formatAddress(activeAddr || "", 8)}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{
                          minHeight: 30,
                        }}
                        alignItems="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Using Arweave Wallet
                        </Typography>
                        <Typography fontFamily={NumberFont} variant="body2">
                          {formatAddress(activeAoAddress, 8)}
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Deposit Button */}
                    {!activeAddr ? (
                      <ContinueButton
                        onClick={() => {
                          ethProvider.setWalletModalVisible(true)
                        }}
                        className="accent"
                      >
                        Connect Ethereum Wallet
                      </ContinueButton>
                    ) : (
                      <ContinueButton
                        onClick={showPopover}
                        disabled={!!validationError}
                        className={!validationError ? "accent" : "secondary"}
                      >
                        {validationError || "Deposit Assets"}
                      </ContinueButton>
                    )}
                  </Stack>
                )}
              </Stack>
            )}

            {/* Withdraw Section */}
            {orderType === "withdraw" && (
              <Stack gap={3}>
                <Typography variant="h6" fontFamily={TitleFont} sx={{ fontWeight: 500 }}>
                  Withdraw Assets
                </Typography>

                <Box
                  sx={{
                    background: "var(--mui-palette-background-default)",
                    borderRadius: "var(--mui-shape-borderRadius)",
                  }}
                >
                  <TokenInputEVM
                    label="You Withdraw"
                    tokenID={fromTokenId}
                    onTokenIdChange={(tokenId) => {
                      if (!tokenId) searchParams.delete("token")
                      else searchParams.set("token", tokenId)
                      setSearchParams(searchParams, { replace: true })
                    }}
                    tokenInfo={fromToken}
                    tokens={tokens}
                    autoFocus
                    onSubmit={showPopover}
                    value={fromValue}
                    onChange={(newValue) => {
                      setFromValue(newValue)
                    }}
                    balance={depositedBalance?.[fromTokenId === DAI.id ? "dai" : "stEth"] || 0n}
                  />
                </Box>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    minHeight: 30,
                  }}
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Using Arweave Wallet
                  </Typography>
                  <Typography fontFamily={NumberFont} variant="body2">
                    {formatAddress(activeAoAddress, 8)}
                  </Typography>
                </Stack>

                {!activeAddr ? (
                  <ContinueButton
                    onClick={() => {
                      ethProvider.setWalletModalVisible(true)
                    }}
                    className="accent"
                  >
                    Connect Ethereum Wallet
                  </ContinueButton>
                ) : (
                  <ContinueButton
                    onClick={showPopover}
                    disabled={!!validationError}
                    className={!validationError ? "accent" : "secondary"}
                  >
                    {validationError || "Withdraw"}
                  </ContinueButton>
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>

      {/* BOTG Distribution Stats Section */}
      <BOTGStatsPanel />

      {/* FAQ Section */}
      <Stack
        component={Paper}
        elevation={0}
        sx={{
          padding: 2,
          mb: 2,
          width: "100%",
        }}
      >
        <Typography variant="h5" fontFamily={TitleFont} gutterBottom>
          About $BOTG Tokens
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle1" fontFamily={TitleFont} gutterBottom>
            What is the BOTG token?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            BOTG is the official token of the Botega platform. 50% of all BOTG tokens can be minted
            by delegating your AO yield to Botega. The emission of BOTG tokens follows a decay
            curve, halving once per year.
            <br />
            For more details on tokenomics, please visit:{" "}
            <a
              href="https://docs.autonomous.finance/products/platforms/botega/botega-tokenomics"
              target="_blank"
              style={{
                color: "var(--mui-palette-text-accent)",
              }}
            >
              Botega Tokenomics Documentation
            </a>
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle1" fontFamily={TitleFont} gutterBottom>
            What utility does the BOTG token provide?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            BOTG tokens offer benefits such as reduced platform fees, buybacks, airdrops, and other
            utilities.
            <br />
            Further details can be found here:{" "}
            <a
              href="https://docs.autonomous.finance/products/platforms/botega/botega-tokenomics/protocol-mechanics-of-the-usdbotg-token"
              target="_blank"
              style={{
                color: "var(--mui-palette-text-accent)",
              }}
            >
              Protocol Mechanics of BOTG Token
            </a>
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle1" fontFamily={TitleFont} gutterBottom>
            How does the Botega team utilize rewards earned by the platform?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rewards earned by the platform are partially reinvested in ongoing platform development,
            and an autonomous agent regularly buys back BOTG tokens.
            <br />
            Learn more here:{" "}
            <a
              href="https://docs.autonomous.finance/products/platforms/botega/botega-tokenomics/protocol-mechanics-of-the-usdbotg-token"
              target="_blank"
              style={{
                color: "var(--mui-palette-text-accent)",
              }}
            >
              Protocol Mechanics of BOTG Token
            </a>
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle1" fontFamily={TitleFont} gutterBottom>
            When will BOTG tokens become transferable?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            $BOTG tokens are expected to become transferable sometime between Q3–Q4 2025.
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle1" fontFamily={TitleFont} gutterBottom>
            How can I earn BOTG tokens?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can earn BOTG tokens by delegating your AO stake to Botega or participating in BOTG
            point campaigns.
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle1" fontFamily={TitleFont} gutterBottom>
            How to import the BOTG token into Wander wallet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Quickly add the BOTG token with our step-by-step docs tutorial:{" "}
            <a
              href="https://docs.autonomous.finance/products/platforms/botega/botega-tokenomics/faqs#how-to-import-the-botg-token-into-wander-wallet"
              target="_blank"
              style={{
                color: "var(--mui-palette-text-accent)",
              }}
            >
              Botega Token FAQs
            </a>
          </Typography>
        </Box>
      </Stack>

      {/* Confirmation Dialog */}
      <Dialog
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            minHeight: 450,
            justifyContent: "center",
          },
        }}
        open={confirmDialogOpen}
        onClose={closePopover}
      >
        <IconButton
          onClick={closePopover}
          size="large"
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        {orderStatus === "pending" ? (
          <Stack gap={1} alignItems="center">
            <BeeLetter />
            <Typography variant="h5" fontFamily={TitleFont}>
              Pending Transaction
            </Typography>
            <Stack gap={1} alignItems="center">
              {orderType === "deposit" ? "Depositing" : "Withdrawing"}
              <TokenPill variant="outlined">
                <TokenLogo tx={fromToken?.logo} alt={`${fromToken?.name} logo`} size="small" />
                <Typography
                  fontFamily={NumberFont}
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {formatNumberAuto(fromValue)}
                </Typography>
                {formatTicker(fromToken?.ticker)}
              </TokenPill>
            </Stack>
          </Stack>
        ) : orderStatus === "success" ? (
          <Stack gap={2} alignItems="center">
            <SuccessIcon width={50} height={50} />
            <Typography variant="h5" fontFamily={TitleFont}>
              {orderType === "deposit" ? "Deposited" : "Withdrew"}
            </Typography>
            <Stack alignItems="center">
              <TokenPill variant="outlined">
                <TokenLogo tx={fromToken?.logo} alt={`${fromToken?.name} logo`} size="small" />
                <Typography
                  fontFamily={NumberFont}
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {formatNumberAuto(fromValue)}
                </Typography>
                {formatTicker(fromToken?.ticker)}
              </TokenPill>
            </Stack>

            {orderType === "deposit" && (
              <Stack spacing={2}>
                <Box sx={{ textAlign: "center", mt: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Deposit successful! After depositing, you can set or update your delegation
                    units.
                  </Typography>
                </Box>
                {delegationMutation.isSuccess ? (
                  <Box sx={{ textAlign: "center", mt: 2, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Delegation successfully set to {delegationPercentage}% of your AO yield.
                    </Typography>
                  </Box>
                ) : (
                  <ContinueButton
                    onClick={() => {
                      setDelegationPercentage(100)
                      delegationMutation.mutate(100)
                    }}
                    className="accent"
                    disabled={delegationMutation.isPending}
                  >
                    {delegationMutation.isPending
                      ? "Setting delegation..."
                      : "Delegate AO to Botega to receive $BOTG"}
                  </ContinueButton>
                )}
              </Stack>
            )}

            <Link
              href={`https://etherscan.io/tx/${orderId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                gap: "0.5rem",
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
              }}
              color="secondary"
            >
              View on Etherscan
              <ShareIcon />
            </Link>
          </Stack>
        ) : (
          <Stack gap={2}>
            {fromToken && (
              <Stack
                gap={1}
                direction="row"
                alignItems="center"
                justifyContent="center"
                width="100%"
              >
                <Typography color="text.secondary">
                  {orderType === "deposit" ? "You Deposit" : "You Withdraw"}
                </Typography>
                <TokenPill variant="outlined">
                  {formatNumberAuto(fromValue)}
                  <TokenLogo tx={fromToken.logo} alt={`${fromToken.name} logo`} size="small" />
                  {formatTicker(fromToken.ticker)}
                </TokenPill>
              </Stack>
            )}

            <div>
              {orderType === "deposit" && (
                <Stack spacing={1}>
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center" }}
                      paragraph
                    >
                      After completing your deposit, you'll be able to set or update your delegation
                      units. Setting delegation to Botega allows you to earn $BOTG tokens.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      mb: 2,
                    }}
                  >
                    <Stack spacing={1.5}>
                      {/* Step 1: Allow spending */}
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                        sx={{
                          opacity: needsAllowance ? 1 : 0.7,
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: needsAllowance ? "primary.main" : "text.disabled",
                            color: "black",
                            flexShrink: 0,
                            mt: 0.5,
                            fontSize: "0.85rem",
                          }}
                        >
                          {!needsAllowance ? <SuccessIcon width={16} height={16} /> : "1"}
                        </Box>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={500}>
                            {!needsAllowance ? "Allowance Approved" : "Approve Token Allowance"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {!needsAllowance
                              ? `You've approved spending ${formatTicker(fromToken?.ticker)} tokens`
                              : `Allow the Botega contract to spend your ${formatTicker(fromToken?.ticker)} tokens`}
                          </Typography>
                        </Stack>
                      </Stack>

                      {/* Connector line */}
                      <Box
                        sx={{
                          width: 1,
                          height: 2,
                          bgcolor: needsAllowance ? "divider" : "success.main",
                          ml: "12px",
                        }}
                      />

                      {/* Step 2: Deposit */}
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                        sx={{
                          opacity: !needsAllowance ? 1 : 0.7,
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: !needsAllowance ? "primary.main" : "text.disabled",
                            color: "white",
                            flexShrink: 0,
                            mt: 0.5,
                            fontSize: "0.85rem",
                          }}
                        >
                          2
                        </Box>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={500}>
                            Deposit Assets
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Deposit your {formatTicker(fromToken?.ticker)} tokens to earn BOTG
                            rewards
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </div>
            {orderError && (
              <Alert type="error" topSpace>
                {orderError}
              </Alert>
            )}
            {!orderStatus && (
              <ContinueButton onClick={handleSubmit} className="accent">
                {orderType === "deposit"
                  ? needsAllowance
                    ? "Sign Allowance Transaction"
                    : "Sign Deposit Transaction"
                  : "Sign Withdraw Transaction"}
              </ContinueButton>
            )}
            {orderStatus === "pendingWalletConfirmation" && (
              <ContinueButton disabled className="secondary">
                {orderType === "deposit"
                  ? needsAllowance
                    ? "Confirm Allowance In Your Wallet..."
                    : "Confirm Deposit In Your Wallet..."
                  : "Confirm Withdraw In Your Wallet..."}
              </ContinueButton>
            )}
            {orderStatus === "error" && (
              <ContinueButton onClick={closePopover} className="secondary">
                Close
              </ContinueButton>
            )}
          </Stack>
        )}
      </Dialog>
    </Page>
  )
}

const Page = styled.div`
  max-width: 740px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const ContinueButton = styled(Button)`
  padding: 1.4rem 1.55rem;
  width: 100%;
  font-size: 1.35rem;
`

const Link = styled.a`
  display: inline-flex;
  align-items: center;
  color: var(--mui-palette-primary-main);
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    color: var(--mui-palette-primary-dark);
    text-decoration: underline;
  }
`

// Removed unused Title component

// Helper function to check transaction receipt
async function checkTransactionReceipt(txHash: string, ethProvider: any) {
  const web3 = new Web3(ethProvider.web3Provider)
  let receipt = null
  const maxTries = 100
  let tries = 0

  while (!receipt && tries < maxTries) {
    receipt = await web3.eth.getTransactionReceipt(txHash)
    if (!receipt) {
      console.log("Transaction is still pending...")
      await new Promise((resolve) => setTimeout(resolve, 1000))
      tries++
    }
  }

  if (receipt && receipt.status) {
    console.log("Transaction was successful!", receipt)
  } else {
    console.warn("Transaction failed or not confirmed after maximum attempts")
  }

  return receipt
}
